import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { selectThemeMode } from '../../redux/slices/themeSlice';
import { getPlatform } from '../../common/firebase';
import { PHONE_MEDIA_QUERY } from '../../common/responsive';
import {
  EVENT_APP_STORE_BUTTON_CLICKED,
  EVENT_PLAY_STORE_BUTTON_CLICKED,
  sendEvent,
} from '../../common/analytics';

const StoreImage = styled.img`
  max-width: ${({ $maxWidth }) => $maxWidth || '200px'};
  height: auto;
  cursor: pointer;
  transition: opacity 0.2s ease;

  @media (hover: hover) {
    &:hover {
      opacity: 0.8;
    }
  }
`;

const StoresContainer = styled.div`
  display: flex;
  gap: ${({ $gap }) => $gap || '20px'};
  flex-direction: row;
  @media ${PHONE_MEDIA_QUERY} {
    flex-direction: column;
    align-items: center;
    gap: ${({ $gap }) => {
      if (!$gap) return '15px';
      // Extract number from gap string (e.g., "20px" -> 20)
      const num = parseFloat($gap);
      const unit = $gap.replace(/[0-9.]/g, '') || 'px';
      return `${num * 0.75}${unit}`;
    }};
  }
`;

const SingleStoreContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  @media ${PHONE_MEDIA_QUERY} {
    width: 100%;
  }
`;

const StoreButtons = ({ maxWidth = '200px', gap = '20px' }) => {
  const themeMode = useSelector(selectThemeMode);
  const platform = getPlatform();
  const isAndroid = platform === 'android';
  const isIOS = platform === 'ios';
  const isDark = themeMode === 'dark';

  const openPlayStore = () => {
    sendEvent(EVENT_PLAY_STORE_BUTTON_CLICKED);
    window.open(
      'https://play.google.com/store/apps/details?id=xyz.deks.app',
      '_blank',
    );
  };

  const openAppStore = () => {
    sendEvent(EVENT_APP_STORE_BUTTON_CLICKED);
    window.open(
      'https://apps.apple.com/us/app/deks-xyz/id6749857801',
      '_blank',
    );
  };

  const getImageSrc = () => {
    if (isAndroid) {
      return isDark
        ? '/download/playstore-dark.webp'
        : '/download/playstore-light.webp';
    } else if (isIOS) {
      return isDark
        ? '/download/appstore-dark.webp'
        : '/download/appstore-light.webp';
    } else {
      // Show both for unknown platforms
      return null;
    }
  };

  const getBothImages = () => {
    return (
      <>
        <StoreImage
          $maxWidth={maxWidth}
          onClick={openPlayStore}
          src={
            isDark
              ? '/download/playstore-dark.webp'
              : '/download/playstore-light.webp'
          }
          alt='Get it on Google Play'
        />
        <StoreImage
          $maxWidth={maxWidth}
          onClick={openAppStore}
          src={
            isDark
              ? '/download/appstore-dark.webp'
              : '/download/appstore-light.webp'
          }
          alt='Download on the App Store'
        />
      </>
    );
  };

  const imageSrc = getImageSrc();

  if (imageSrc) {
    return (
      <SingleStoreContainer>
        <StoreImage
          $maxWidth={maxWidth}
          onClick={() => {
            if (platform === 'android') {
              openPlayStore();
            } else if (platform === 'ios') {
              openAppStore();
            }
          }}
          src={imageSrc}
          alt={
            platform === 'android'
              ? 'Get it on Google Play'
              : 'Download on the App Store'
          }
        />
      </SingleStoreContainer>
    );
  }

  return <StoresContainer $gap={gap}>{getBothImages()}</StoresContainer>;
};

export default StoreButtons;
